import React, { useEffect, useState } from 'react';

// Parcours de connexion du compteur via le service Data Connect d'Enedis.
// Il suit le contrat Data Connect signé le 19 octobre 2026 (annexe 3, § 3.2.4 et 3.2.5) :
//   1. présenter le service et l'usage des données AVANT le bouton de partage ;
//   2. afficher les conditions d'éligibilité au même endroit ;
//   3. bouton officiel Data Connect, non modifié, consentement de 3 ans maximum ;
//   4. message de succès ou d'échec au retour d'Enedis ;
//   + un message clair pour les cas exclus.
// Règles de marque : « compteur Linky » (jamais « le Linky »), « Enedis » sans majuscules
// ni accent, aucune formule laissant croire qu'Enedis soutient ou a réalisé Nowatt.

const API = process.env.REACT_APP_API_URL || 'https://api.nowatt.app';

// Durée de l'accord demandé à l'utilisateur (le contrat fixe un maximum de 3 ans)
export const DUREE_CONSENTEMENT_ANS = 3;

// Visuel OFFICIEL du bouton Data Connect, à télécharger depuis le Portail Bénéficiaire
// d'Enedis et à placer dans /public sans aucune retouche (forme, couleurs, proportions).
// Tant qu'il est absent, le bouton reste désactivé : le parcours ne peut pas être mis en ligne.
export const BOUTON_OFFICIEL = null; // ex. '/enedis/data-connect-bouton.svg'

// Interrupteur unique : le parcours n'est proposé dans l'app que lorsque le branchement
// serveur (phase 3) est en production ET que le bouton officiel est intégré.
export const CONNEXION_PRETE = false && Boolean(BOUTON_OFFICIEL);

// Route du serveur qui démarre l'accord chez Enedis (phase 3, à construire)
const URL_DEPART = `${API}/dataconnect/start`;

const C = {
  bg: '#06080C', card: '#0D1117', lime: '#C8F135', text: '#EEF2FF',
  muted: 'rgba(238,242,255,0.55)', faint: 'rgba(238,242,255,0.35)', border: 'rgba(238,242,255,0.08)',
  green: '#4ade80', amber: '#f59e0b', red: '#ff6b6b',
  title: { fontFamily: "'Lexend', sans-serif" }, body: { fontFamily: "'Plus Jakarta Sans', sans-serif" },
};

const ESPACE_ENEDIS = 'https://mon-compte-particulier.enedis.fr/';

// Seules les données utiles au service sont demandées (contrat, art. 3.2 et RGPD)
const DONNEES = [
  ['Votre contrat', 'puissance souscrite, option tarifaire, plages d\'heures creuses'],
  ['L\'adresse du logement', 'commune et code postal, pour la météo locale et les comparaisons'],
  ['Votre consommation par jour', 'et la puissance maximale atteinte chaque jour'],
  ['Votre courbe de consommation', 'mesurée toutes les 30 minutes, si elle est activée sur votre compteur'],
];

const USAGES = [
  'Comparer les offres sur votre consommation réelle, et non sur une estimation',
  'Vérifier si votre puissance souscrite est adaptée',
  'Vous alerter quand votre consommation dérape ou qu\'une offre plus intéressante apparaît',
];

const CONDITIONS = [
  ['Un compteur Linky', 'votre logement est équipé d\'un compteur communicant'],
  ['Un contrat d\'électricité à votre nom', 'pour ce logement, actuellement en cours'],
  ['Un logement raccordé au réseau Enedis', 'c\'est le cas d\'environ 95 % des foyers en France métropolitaine'],
  ['Une puissance de 36 kVA ou moins', 'c\'est le cas de presque tous les logements'],
  ['Un espace client Enedis', 'gratuit, il se crée en quelques minutes'],
];

// ── Petits composants ──
const Etapes = ({ n }) => (
  <div style={{ display: 'flex', gap: 6, marginBottom: 22 }} aria-label={`Étape ${n} sur 3`}>
    {[1, 2, 3].map((i) => (
      <div key={i} style={{ flex: 1, height: 4, borderRadius: 4, background: i <= n ? C.lime : C.border }} />
    ))}
  </div>
);

const Titre = ({ children }) => (
  <h1 style={{ ...C.title, fontSize: '1.45rem', fontWeight: 800, lineHeight: 1.25, margin: '0 0 10px', color: C.text }}>{children}</h1>
);

const Texte = ({ children, style }) => (
  <p style={{ ...C.body, fontSize: '0.9rem', lineHeight: 1.65, color: C.muted, margin: '0 0 14px', ...style }}>{children}</p>
);

const Bloc = ({ titre, children }) => (
  <section style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: '16px 18px', marginBottom: 12 }}>
    <h2 style={{ ...C.title, fontSize: '0.95rem', fontWeight: 700, margin: '0 0 10px', color: C.text }}>{titre}</h2>
    {children}
  </section>
);

const Ligne = ({ fort, detail, icone = '·', couleur = C.lime }) => (
  <li style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '6px 0', ...C.body, fontSize: '0.86rem', lineHeight: 1.55 }}>
    <span aria-hidden="true" style={{ color: couleur, fontWeight: 800, flexShrink: 0, width: 14, textAlign: 'center' }}>{icone}</span>
    <span style={{ color: C.text }}>{fort}{detail && <span style={{ color: C.muted }}> — {detail}</span>}</span>
  </li>
);

const BoutonPrincipal = ({ children, onClick, disabled }) => (
  <button onClick={onClick} disabled={disabled} className="cc-btn" style={{
    width: '100%', background: disabled ? 'rgba(200,241,53,0.35)' : C.lime, color: C.bg, border: 'none', borderRadius: 12,
    padding: '15px 18px', fontWeight: 700, fontSize: '0.95rem', cursor: disabled ? 'not-allowed' : 'pointer', ...C.title,
  }}>{children}</button>
);

const BoutonSecondaire = ({ children, onClick }) => (
  <button onClick={onClick} className="cc-btn2" style={{
    width: '100%', background: 'transparent', color: C.muted, border: `1px solid ${C.border}`, borderRadius: 12,
    padding: '13px 18px', fontWeight: 600, fontSize: '0.88rem', cursor: 'pointer', marginTop: 10, ...C.body,
  }}>{children}</button>
);

const Lien = ({ href, children }) => (
  <a href={href} target="_blank" rel="noopener noreferrer" style={{ color: C.lime }}>{children}</a>
);

// Bouton d'action Data Connect : uniquement le visuel officiel fourni par Enedis
function BoutonDataConnect() {
  if (!BOUTON_OFFICIEL) {
    return (
      <div role="note" style={{ border: `1px dashed ${C.amber}`, borderRadius: 12, padding: '16px', textAlign: 'center', ...C.body, fontSize: '0.82rem', color: C.amber, lineHeight: 1.5 }}>
        Emplacement du bouton officiel Enedis.<br />
        À intégrer depuis le Portail Bénéficiaire avant toute mise en ligne.
      </div>
    );
  }
  const depart = `${URL_DEPART}?duree=${DUREE_CONSENTEMENT_ANS}`;
  return (
    <a href={depart} style={{ display: 'block', textAlign: 'center' }}>
      <img src={BOUTON_OFFICIEL} alt="Accéder à mes données avec Enedis" style={{ maxWidth: '100%', height: 'auto' }} />
    </a>
  );
}

// ── Les écrans ──
function Information({ suite }) {
  return (
    <>
      <Etapes n={1} />
      <Titre>Connectez votre compteur Linky</Titre>
      <Texte>
        En quelques clics, Nowatt peut s'appuyer sur votre <strong style={{ color: C.text }}>vraie consommation</strong> plutôt
        que sur une estimation. Voici exactement ce que cela implique.
      </Texte>

      <Bloc titre="À quoi serviront vos données">
        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {USAGES.map((u) => <Ligne key={u} icone="✓" fort={u} />)}
        </ul>
      </Bloc>

      <Bloc titre="Les données demandées">
        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {DONNEES.map(([f, d]) => <Ligne key={f} fort={f} detail={d} />)}
        </ul>
        <Texte style={{ margin: '8px 0 0', fontSize: '0.8rem' }}>
          Rien d'autre : ni vos coordonnées, ni vos identifiants Enedis, que Nowatt ne voit jamais.
        </Texte>
      </Bloc>

      <Bloc titre="Votre accord, vos règles">
        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          <Ligne icone="•" fort={`Accord valable ${DUREE_CONSENTEMENT_ANS} ans au maximum`} />
          <Ligne icone="•" fort="Retirable à tout moment" detail="depuis votre espace client Enedis ou depuis Mon compte" />
          <Ligne icone="•" fort="Jamais vendues ni partagées" detail="vos données servent uniquement à votre service Nowatt" />
          <Ligne icone="•" fort="Supprimées" detail="si vous retirez votre accord ou fermez votre compte" />
        </ul>
      </Bloc>

      <Texte style={{ fontSize: '0.78rem', color: C.faint }}>
        Nowatt est un service indépendant. Le partage de vos données est réalisé par Enedis, gestionnaire du réseau
        public de distribution d'électricité, uniquement avec votre accord. Détails dans notre politique de confidentialité.
      </Texte>

      <BoutonPrincipal onClick={suite}>Continuer</BoutonPrincipal>
    </>
  );
}

function Eligibilite({ suite, exclu, retour }) {
  return (
    <>
      <Etapes n={2} />
      <Titre>Vérifions que votre logement est éligible</Titre>
      <Texte>Le partage de données par Enedis n'est possible que si vous remplissez toutes ces conditions :</Texte>

      <Bloc titre="Conditions à remplir">
        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {CONDITIONS.map(([f, d]) => <Ligne key={f} icone="✓" fort={f} detail={d} />)}
        </ul>
      </Bloc>

      <Texte style={{ fontSize: '0.82rem' }}>
        Pas encore d'espace client Enedis ? <Lien href={ESPACE_ENEDIS}>Créez-le gratuitement</Lien>, puis revenez ici.
      </Texte>

      <BoutonPrincipal onClick={suite}>Je remplis ces conditions</BoutonPrincipal>
      <BoutonSecondaire onClick={exclu}>Je ne suis pas sûr, ou pas concerné</BoutonSecondaire>
      <BoutonSecondaire onClick={retour}>Retour</BoutonSecondaire>
    </>
  );
}

function Partage({ retour }) {
  return (
    <>
      <Etapes n={3} />
      <Titre>Donnez votre accord chez Enedis</Titre>
      <Texte>
        En cliquant ci-dessous, vous êtes redirigé vers <strong style={{ color: C.text }}>votre espace client Enedis</strong>.
        Connectez-vous, vérifiez les données demandées et validez. Vous revenez ensuite automatiquement sur Nowatt.
      </Texte>

      <Bloc titre="Récapitulatif">
        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          <Ligne icone="•" fort="Données" detail="contrat, adresse du logement, consommation par jour et courbe de consommation" />
          <Ligne icone="•" fort="Durée" detail={`${DUREE_CONSENTEMENT_ANS} ans maximum, retirable à tout moment`} />
          <Ligne icone="•" fort="Usage" detail="uniquement pour votre service Nowatt" />
        </ul>
      </Bloc>

      <div style={{ margin: '18px 0 6px' }}><BoutonDataConnect /></div>
      <BoutonSecondaire onClick={retour}>Retour</BoutonSecondaire>
    </>
  );
}

function CasExclus({ fermer, retour }) {
  return (
    <>
      <Titre>Les cas où la connexion n'est pas possible</Titre>
      <Texte>Pas d'inquiétude : Nowatt reste utilisable avec vos informations saisies à la main.</Texte>

      <Bloc titre="Vous n'avez pas d'espace client Enedis">
        <Texte style={{ margin: 0, fontSize: '0.85rem' }}>
          C'est le cas le plus fréquent, et le plus simple à régler : <Lien href={ESPACE_ENEDIS}>créez votre espace client Enedis</Lien>,
          gratuitement, avec la référence de votre compteur (elle figure sur votre facture). Revenez ensuite connecter votre compteur.
        </Texte>
      </Bloc>

      <Bloc titre="Votre logement n'est pas raccordé au réseau Enedis">
        <Texte style={{ margin: 0, fontSize: '0.85rem' }}>
          Environ 5 % des foyers dépendent d'un distributeur local (par exemple à Strasbourg, Grenoble ou Metz). Votre facture
          d'électricité indique le nom du gestionnaire de réseau. Dans ce cas, le partage par Enedis n'est pas possible :
          Nowatt continue de fonctionner avec la consommation que vous avez saisie.
        </Texte>
      </Bloc>

      <Bloc titre="Le contrat d'électricité n'est pas à votre nom">
        <Texte style={{ margin: 0, fontSize: '0.85rem' }}>
          Seul le titulaire du contrat peut donner son accord. S'il s'agit d'un proche (conjoint, colocataire…), c'est
          depuis son compte Nowatt et son espace client Enedis que la connexion doit se faire.
        </Texte>
      </Bloc>

      <Bloc titre="Votre logement n'a pas encore de compteur Linky">
        <Texte style={{ margin: 0, fontSize: '0.85rem' }}>
          Sans compteur communicant, aucune donnée ne peut être transmise à distance. Nowatt fonctionne alors avec vos
          informations saisies.
        </Texte>
      </Bloc>

      <BoutonPrincipal onClick={fermer}>Continuer avec mes informations saisies</BoutonPrincipal>
      <BoutonSecondaire onClick={retour}>Revenir aux conditions</BoutonSecondaire>
    </>
  );
}

// Retour d'Enedis : le serveur renvoie vers l'app avec ?compteur=connecte|refuse|erreur
const RESULTATS = {
  connecte: {
    icone: '✓', couleur: C.green, titre: 'Votre compteur est connecté',
    texte: 'Merci ! Vos premières données arriveront dès demain. Votre courbe de consommation détaillée peut prendre quelques jours à apparaître, le temps qu\'Enedis la transmette.',
    bouton: 'Voir mon compteur',
  },
  refuse: {
    icone: '–', couleur: C.amber, titre: 'Connexion annulée',
    texte: 'Vous n\'avez pas donné votre accord chez Enedis : aucune donnée n\'a été partagée. Vous pouvez recommencer quand vous le souhaitez, Nowatt reste utilisable avec vos informations saisies.',
    bouton: 'Fermer',
  },
  erreur: {
    icone: '!', couleur: C.red, titre: 'La connexion n\'a pas abouti',
    texte: 'Un problème est survenu pendant l\'échange avec Enedis. Aucune donnée n\'a été partagée. Réessayez dans quelques minutes ; si le problème persiste, écrivez-nous à contact@nowatt.app.',
    bouton: 'Fermer',
  },
};

function Resultat({ type, fermer, recommencer }) {
  const r = RESULTATS[type] || RESULTATS.erreur;
  return (
    <div style={{ textAlign: 'center', paddingTop: 30 }}>
      <div aria-hidden="true" style={{
        width: 64, height: 64, borderRadius: '50%', margin: '0 auto 20px', display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: `${r.couleur}1f`, border: `2px solid ${r.couleur}`, color: r.couleur, fontSize: '1.7rem', fontWeight: 800, ...C.title,
      }}>{r.icone}</div>
      <Titre>{r.titre}</Titre>
      <Texte style={{ maxWidth: 440, margin: '0 auto 26px' }}>{r.texte}</Texte>
      <BoutonPrincipal onClick={fermer}>{r.bouton}</BoutonPrincipal>
      {type !== 'connecte' && <BoutonSecondaire onClick={recommencer}>Recommencer</BoutonSecondaire>}
    </div>
  );
}

// ── Composant principal : plein écran, par-dessus l'app ──
export default function ConnexionCompteur({ resultat = null, onClose }) {
  const [etape, setEtape] = useState(resultat ? 'resultat' : 'information');

  useEffect(() => { window.scrollTo(0, 0); }, [etape]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div role="dialog" aria-modal="true" aria-label="Connecter mon compteur" style={{
      position: 'fixed', inset: 0, zIndex: 300, background: C.bg, color: C.text, overflowY: 'auto', ...C.body,
    }}>
      <style>{`
        .cc-btn:hover:not(:disabled) { filter: brightness(1.07); }
        .cc-btn2:hover { color: ${C.text}; border-color: rgba(238,242,255,0.2); }
        .cc-btn:focus-visible, .cc-btn2:focus-visible, .cc-close:focus-visible { outline: 2px solid ${C.lime}; outline-offset: 2px; }
      `}</style>
      <div style={{ maxWidth: 560, margin: '0 auto', padding: '18px 16px 48px', boxSizing: 'border-box' }}>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 6 }}>
          <button className="cc-close" onClick={onClose} aria-label="Fermer" style={{
            background: 'transparent', border: `1px solid ${C.border}`, color: C.muted, borderRadius: 10,
            width: 38, height: 38, fontSize: '1.1rem', cursor: 'pointer',
          }}>✕</button>
        </div>

        {etape === 'information' && <Information suite={() => setEtape('eligibilite')} />}
        {etape === 'eligibilite' && (
          <Eligibilite suite={() => setEtape('partage')} exclu={() => setEtape('exclus')} retour={() => setEtape('information')} />
        )}
        {etape === 'partage' && <Partage retour={() => setEtape('eligibilite')} />}
        {etape === 'exclus' && <CasExclus fermer={onClose} retour={() => setEtape('eligibilite')} />}
        {etape === 'resultat' && <Resultat type={resultat} fermer={onClose} recommencer={() => setEtape('information')} />}
      </div>
    </div>
  );
}
